import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Badge,
  Banner,
  BlockStack,
  Box,
  Button,
  IndexTable,
  InlineStack,
  Modal,
  Pagination,
  Select,
  Text,
  TextField,
  Tooltip,
} from '@shopify/polaris';
import { ExportIcon } from '@shopify/polaris-icons';
import apiClient from '../utils/api';
import { saveBlob, datedFilename } from '../utils/download';
import RunHealthBanner, { RUN_TYPES } from './RunHealthBanner';
import { DEFAULT_PAGE_SIZE, PageSizeSelect } from '../utils/table';

// 10 rows, Bhavjot's call. This table is wide — ten columns, two dates and a two-line SKU
// cell — so twenty rows made it the whole screen on both placements. It is now the SEED for
// `pageSize` rather than the size itself: the table pages SERVER-side, so the chosen count has
// to go out as the API's `limit` — slicing the response would show ten of the fifty rows the
// server sent and label it "Page 1 of 5".
const PAGE_SIZE = DEFAULT_PAGE_SIZE;

/**
 * The SKU comparison table — Shopify stock beside retailer stock, one row per SKU per retailer.
 *
 * ONE component, two placements: a brand's Retail Connect tab passes a brandId and gets that
 * brand's SKUs; the Retailer Accounts page passes none and gets every brand's, with a Brand
 * column and a brand filter. A second copy of a ten-column table would drift within a week.
 *
 * The card, its heading and its buttons belong to the host — this renders the filters, the
 * table and its pagination, so each screen keeps the header it already has.
 *
 * TWO DATES, NEVER ONE. Both sides are read on their own schedule: the retailer's offers every
 * 6 hours or at 02:00, the Shopify catalogue at 03:00. So a difference between the two stock
 * figures is a difference between two readings of DIFFERENT ages, and printing it without both
 * dates would be read as a live discrepancy. Every row carries both, and so does the summary.
 */

const STATUS_OPTIONS = [
  { label: 'All SKUs', value: 'all' },
  { label: 'Listed', value: 'listed' },
  { label: 'Not listed', value: 'not_listed' },
  { label: 'Inactive at retailer', value: 'inactive' },
  { label: 'Stock differs', value: 'mismatch' },
  { label: 'Push queued', value: 'pending' },
  { label: 'Push rejected', value: 'rejected' },
];

// "1 Sep, 12:18". Long enough to compare two readings, short enough for two lines in a cell.
const shortWhen = (value) => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return `${d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}, `
    + `${d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}`;
};

/**
 * A row's identity — and brandId is not optional in it.
 *
 * The backend grain is (brandId, sku, marketplaceId): the CTE groups by brand and SKU, so on
 * the ALL-BRANDS view two brands whose stores carry the same SKU string produce two rows with
 * an identical sku AND an identical marketplaceId (or two `sku:all` rows when neither is
 * listed). Keying without brandId collided them, and React silently rendered one and dropped
 * the other — on a screen whose whole job is to show every listing. Pressing Re-read on one of
 * a colliding pair also spun both.
 */
const rowKey = (row) => `${row.brandId}:${row.sku}:${row.marketplaceId || 'all'}`;

const signed = (n) => (n > 0 ? `+${n}` : String(n));

const num = (n) => new Intl.NumberFormat().format(n);

export default function SkuMapTable({
  brandId = null,
  showBrandColumn = false,
  brandOptions = [],
  marketplaceOptions = [],
  refreshToken = 0,
  // Why the push controls are unavailable, from the host, which already reads the write
  // toggles. Passed in rather than fetched here so the button explains itself instead of
  // looking live and failing on click — the route refuses either way.
  pushDisabledReason = null,
}) {
  const [data, setData] = useState(null);
  // The preview a confirmation is built from. Null when no confirmation is open.
  const [pushPreview, setPushPreview] = useState(null);
  const [pushBusy, setPushBusy] = useState(false);
  const [pushingRow, setPushingRow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [note, setNote] = useState(null);

  const [page, setPage] = useState(1);
  // Seeded with this table's own existing count, so neither placement changes size unasked.
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');
  const [marketplaceId, setMarketplaceId] = useState('');
  const [brandFilter, setBrandFilter] = useState('');
  // MOST RECENTLY READ FIRST, by default.
  //
  // Sorting by SKU put an arbitrary slice of 6,000 rows on page 1 — the lowest SKU numbers,
  // which say nothing about whether anyone should look at them. The retailer read date is the
  // one ordering that answers "what changed", so a re-read or a fresh offer fetch surfaces
  // its rows at the top instead of leaving them somewhere on page 400.
  //
  // Seeded rather than left blank so the heading arrow states what the rows already are. A
  // table that arrives sorted under an unmarked heading reads as unsorted, and the first
  // click then appears to do nothing because it only confirms the order.
  //
  // NULLS LAST is applied by the service for every sort, so SKUs the retailer has never read
  // stay at the end in both directions rather than leading a descending sort.
  const [sortBy, setSortBy] = useState('miraklVerifiedAt');
  const [sortDirection, setSortDirection] = useState('desc');
  const [rereading, setRereading] = useState(null);

  // Debounced, so typing a SKU is one request rather than one per keystroke against a table
  // that scans the whole catalogue.
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Any filter change starts again at page 1. Without this, narrowing a filter while on page 12
  // shows an empty table and reads as "no results". A new ROW COUNT belongs in the same reset:
  // page 12 of a ten-row window is inside page 3 of a fifty-row one, so keeping the number asks
  // the API for a page past the end and the table comes back empty.
  useEffect(() => {
    setPage(1);
  }, [search, status, marketplaceId, brandFilter, sortBy, sortDirection, pageSize]);

  // Only the NEWEST request may write state.
  //
  // The page reset lives in its own effect while `load` depends on `page`, so both run in the
  // same commit: on page 12, committing a new search term queues setPage(1) AND fires a
  // request for page 12, then the next commit fires page 1. Two requests, no cancellation, no
  // ordering guarantee — so the slower one won and page-12 rows sat under "Page 1 of 3".
  const requestSeq = useRef(0);

  const load = useCallback(async () => {
    const seq = ++requestSeq.current;
    setLoading(true);
    try {
      const params = {
        page, limit: pageSize, search, status, marketplaceId, sortBy, sortDirection,
      };
      const res = brandId
        ? await apiClient.getBrandSkuMapRows(brandId, params)
        : await apiClient.getSkuMapRows({ ...params, brandId: brandFilter });
      if (seq !== requestSeq.current) return;
      setData(res);
      setError(null);
    } catch (err) {
      if (seq !== requestSeq.current) return;
      setError(err.message || 'Failed to load the SKU comparison');
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, [brandId, page, pageSize, search, status, marketplaceId, brandFilter, sortBy, sortDirection]);

  // refreshToken belongs on the effect, not in load's dependencies: the host bumps it after
  // "Fetch inventories" or "Refresh SKU Map" to pull the new figures in, and load itself does
  // not read it.
  useEffect(() => {
    load();
  }, [load, refreshToken]);

  const handleReread = async (row) => {
    const key = rowKey(row);
    setRereading(key);
    setNote(null);
    try {
      const res = await apiClient.refreshOneSku({
        sku: row.sku,
        brandId: row.brandId,
        // Null on a "Not listed" row, which re-reads every retailer this brand is connected
        // to — the only way to answer "is it really not listed anywhere?"
        marketplaceId: row.marketplaceId || null,
      });
      setNote({ tone: 'info', text: res.message || 'Re-read' });
      await load();
    } catch (err) {
      setNote({ tone: 'critical', text: err.message || 'Could not re-read that SKU' });
    } finally {
      setRereading(null);
    }
  };

  /**
   * Bulk push: preview first, then confirm.
   *
   * Scoped to ONE retailer because a stock import is one import per account — "all
   * retailers" would be several imports against a once-a-minute ceiling, and a single
   * confirmation covering several retailers' live listings is not a confirmation anyone
   * can reason about.
   */
  const openBulkPush = async () => {
    setPushBusy(true);
    setNote(null);
    try {
      const preview = await apiClient.queueRetailerInventoryPush(marketplaceId, {
        onlyDiffering: true,
        dryRun: true,
      });
      setPushPreview(preview);
    } catch (err) {
      setNote({ tone: 'critical', text: err.message || 'Could not work out what would be pushed' });
    } finally {
      setPushBusy(false);
    }
  };

  const confirmBulkPush = async () => {
    setPushBusy(true);
    try {
      const res = await apiClient.queueRetailerInventoryPush(marketplaceId, { onlyDiffering: true });
      setPushPreview(null);
      setNote({ tone: res.queued > 0 ? 'success' : 'info', text: res.message });
      await load();
    } catch (err) {
      setNote({ tone: 'critical', text: err.message || 'The push was not queued' });
    } finally {
      setPushBusy(false);
    }
  };

  /**
   * One row, sent immediately.
   *
   * No confirmation here, unlike the bulk push: it is a single SKU whose two figures are on
   * the row being clicked, and another push corrects it. The bulk action commits up to
   * thousands of figures the operator cannot see, which is what the dialog is for.
   */
  const handleRowPush = async (row) => {
    const key = rowKey(row);
    setPushingRow(key);
    setNote(null);
    try {
      const res = await apiClient.queueRetailerInventoryPush(row.marketplaceId, {
        shopSkus: [row.sku],
      });
      // The REAL reason nothing was sent. This said "the retailer already holds this
      // quantity" whatever had happened, so a SKU the retailer does not list, one belonging
      // to another brand, and one with no Shopify figure yet all reported a matching stock
      // level that was never compared — and the operator had no reason to look further.
      const why = res.unlistedRequested?.length
        ? `${row.sku} is not listed at ${res.marketplaceName}, so there is no offer to update.`
        : res.unownedRequested?.length
          ? `${row.sku} is listed at ${res.marketplaceName} under another brand, so it is not ours to change.`
          : res.noMirrorRequested?.length
            ? `${row.sku} has no Shopify stock figure yet — run Fetch inventories first.`
            : res.capped?.count
              ? `${row.sku} was beyond the ceiling for one import. Try again in a moment.`
              : `Nothing sent for ${row.sku} — the retailer already holds this quantity.`;
      setNote({
        tone: res.queued > 0 ? 'success' : 'info',
        text: res.queued > 0 ? `${row.sku} queued and sent to ${res.marketplaceName}.` : why,
      });
      await load();
    } catch (err) {
      setNote({ tone: 'critical', text: err.message || 'Could not push that SKU' });
    } finally {
      setPushingRow(null);
    }
  };

  const columns = useMemo(() => [
    { key: 'sku', title: 'SKU', sort: 'sku', labels: { ascending: 'A–Z', descending: 'Z–A' },
      tip: "The brand's Shopify SKU. One row per SKU, aggregated across every variant carrying it — Shopify permits duplicates, and the stock shown is their sum." },
    ...(showBrandColumn ? [{ key: 'brand', title: 'Brand', sort: 'brandName',
      labels: { ascending: 'A–Z', descending: 'Z–A' },
      tip: "The 2W brand whose Shopify catalogue this SKU comes from. A SKU code shared by two brands appears as two rows, because they are two different products." }] : []),
    {
      key: 'retailer',
      title: 'Retailer',
      sort: 'marketplaceName',
      labels: { ascending: 'A–Z, not listed last', descending: 'Z–A, not listed last' },
      tip: 'Which retailer lists this SKU. A SKU listed at three retailers appears three times, '
        + 'once per retailer. Not listed means none of the connected retailers carry it — those '
        + 'rows have no retailer, so sorting this column groups them together at the end rather '
        + 'than scattering them through the names.',
    },
    {
      key: 'shopify',
      title: 'Shopify stock',
      sort: 'shopifyQuantity',
      labels: { ascending: 'Lowest first', descending: 'Highest first' },
      tip: 'What 2W last read from Shopify. The catalogue is re-read once a day, so this is the '
        + 'figure as of the date in the As of column — not this moment.',
    },
    {
      key: 'mirakl',
      title: 'Retailer stock',
      sort: 'miraklQuantity',
      labels: { ascending: 'Lowest first', descending: 'Highest first' },
      tip: 'What the retailer last told us it holds. It does NOT change when we send a new stock '
        + 'figure, so a push we have already made will not appear here until the retailer is read '
        + 'again. Check the Pending column before treating this as out of date.',
    },
    {
      key: 'difference',
      title: 'Difference',
      sort: 'difference',
      labels: { ascending: 'Oversell risk first', descending: 'Missed sales first' },
      tip: 'Shopify stock minus retailer stock, using the two readings on this row — which were '
        + 'taken at different times. A NEGATIVE number is the retailer advertising stock Shopify '
        + 'does not have, so it can take an order you cannot fill; a positive one is stock the '
        + 'retailer is not offering. A number here is a reason to look, not proof of a problem.',
    },
    { key: 'status', title: 'Status', sort: 'status', labels: { ascending: 'A–Z', descending: 'Z–A' },
      tip: 'Whether the retailer carries this SKU, and whether the listing is active. As of the retailer’s last catalogue read, not live.' },
    {
      key: 'asof',
      // The cell holds TWO dates, so the heading says which one the sort uses. An unlabelled
      // sort on a two-value cell reads as broken: half the rows look out of order because the
      // reader is checking the other date.
      title: 'As of (retailer)',
      sort: 'miraklVerifiedAt',
      labels: {
        ascending: 'Oldest retailer read first',
        descending: 'Newest retailer read first',
      },
      tip: 'When each side was last read. The two are on separate schedules and are almost never '
        + 'the same age, which is why both are shown. SORTS ON THE RETAILER READ — the lower '
        + 'date — because that is the one that varies per SKU and says how stale the comparison '
        + 'is; the Shopify catalogue is mirrored in one nightly pass, so its date is very nearly '
        + 'the same on every row and sorting by it would order almost nothing. SKUs no retailer '
        + 'has ever been read for have no retailer date and sort last either way.',
    },
    {
      key: 'push',
      title: 'Last push',
      // Ordered by WHEN the push was queued. The badge state is a name, and its alphabetical
      // order (Rejected, Queued, Unconfirmed, Attempted) carries no meaning for a reader.
      sort: 'pendingQueuedAt',
      labels: { ascending: 'Oldest queued first', descending: 'Newest queued first' },
      tip: 'What happened to the stock figure we last sent this retailer for this SKU. '
        + 'A time on its own means it reached them and they applied it, and nothing is '
        + 'outstanding. Queued or Attempted usually explains a difference — the change is '
        + 'known and has not landed yet. Rejected means the retailer refused it, with the '
        + 'reason on the badge. A time UNDER a badge is the send before the one the badge '
        + 'describes, so a queued SKU still says whether it has ever reached the retailer. '
        + 'A dash means no stock has ever been sent for this SKU, which is normal for one '
        + 'whose stock has not changed since it was connected. Sorting orders by when the '
        + 'push was queued, and SKUs with no push sort last in both directions rather than '
        + 'filling the first page with dashes.',
    },
    // A normal visible heading, like the Actions column on every other table in this
    // console. It carried `hidden: true` with a comment saying Polaris would render the
    // title for screen readers and hide it visually — but the headings mapping below never
    // forwarded `hidden`, so the flag did nothing and the heading was visible anyway. The
    // comment described behaviour that never happened. Made explicit rather than fixed the
    // other way, because a visually hidden heading also has nothing for a tooltip to hang
    // off, and every other Actions column here is visible and tipped.
    {
      key: 'action',
      title: 'Actions',
      tip: 'Re-reads this one SKU from the retailer, so the row can be checked without waiting for the next catalogue refresh. It is a read — nothing here changes anything at the retailer.',
    },
  ], [showBrandColumn]);

  const headings = columns.map((c) => (
    c.tip
      ? { id: c.key, title: c.title, tooltipContent: c.tip, tooltipWidth: 'wide' }
      : { id: c.key, title: c.title }
  ));
  const sortable = columns.map((c) => Boolean(c.sort));
  // Required for a sortable heading to keep its own tooltip: without sortToggleLabels, Polaris
  // returns the bare sort button and drops tooltipContent (IndexTable.js:478).
  const sortToggleLabels = columns.reduce((acc, c, i) => {
    if (c.sort) acc[i] = c.labels || { ascending: 'Ascending', descending: 'Descending' };
    return acc;
  }, {});
  const sortColumnIndex = columns.findIndex((c) => c.sort === sortBy);

  const rows = data?.rows || [];
  const summary = data?.summary || null;
  const pagination = data?.pagination || { page: 1, totalPages: 1, totalCount: 0 };

  /**
   * The download. Sends the table's own filters and no page, so the file is the whole
   * matching set rather than the 25 rows on screen — which is the point of having it.
   */
  const [exporting, setExporting] = useState(false);
  const exportCsv = useCallback(async () => {
    setExporting(true);
    try {
      const blob = await apiClient.exportSkuMap({
        brandId: brandId || brandFilter,
        marketplaceId, search, status, sortBy, sortDirection,
      });
      saveBlob(blob, datedFilename('sku-map'));
    } catch (err) {
      setError(err.message || 'Failed to export the SKU comparison');
    } finally {
      setExporting(false);
    }
  }, [brandId, brandFilter, marketplaceId, search, status, sortBy, sortDirection]);
  const unmatched = data?.unmatchedAtRetailer || [];
  const filtered = Boolean(search || marketplaceId || brandFilter || status !== 'all');
  // The status filter specifically: it is the one the summary line does not reflect.
  const statusFilterActive = status !== 'all';

  const brandSelectOptions = [{ label: 'All brands', value: '' }, ...brandOptions];
  const retailerSelectOptions = [{ label: 'All retailers', value: '' }, ...marketplaceOptions];

  return (
    <BlockStack gap="300">
      {error && (
        <Banner tone="critical" onDismiss={() => setError(null)}>{error}</Banner>
      )}
      {note && (
        <Banner tone={note.tone} onDismiss={() => setNote(null)}>{note.text}</Banner>
      )}

      {/* Both sides of the comparison, because a difference on this screen is only as good as
          the two readings behind it. A short or failed run on EITHER side makes every
          difference here suspect, and the numbers alone cannot say which side is wrong.
          marketplaceId is the filter's value and may be '' for "all retailers" — passed as
          null then, which asks about the newest run of each type rather than one account's. */}
      <RunHealthBanner
        marketplaceId={marketplaceId || null}
        brandId={brandId || null}
        types={RUN_TYPES.SKU_MAP}
      />

      <InlineStack gap="200" wrap blockAlign="center">
        <Box width="220px">
          <TextField
            label="Search SKUs"
            labelHidden
            placeholder="Search SKU or product"
            value={searchInput}
            onChange={setSearchInput}
            autoComplete="off"
            clearButton
            onClearButtonClick={() => setSearchInput('')}
          />
        </Box>
        <Box width="180px">
          <Select label="Status" labelHidden options={STATUS_OPTIONS} value={status} onChange={setStatus} />
        </Box>
        {marketplaceOptions.length > 0 && (
          <Box width="180px">
            <Select
              label="Retailer"
              labelHidden
              options={retailerSelectOptions}
              value={marketplaceId}
              onChange={setMarketplaceId}
            />
          </Box>
        )}
        {showBrandColumn && brandOptions.length > 0 && (
          <Box width="180px">
            <Select
              label="Brand"
              labelHidden
              options={brandSelectOptions}
              value={brandFilter}
              onChange={setBrandFilter}
            />
          </Box>
        )}
        {filtered && (
          <Button
            variant="plain"
            onClick={() => {
              setSearchInput('');
              setStatus('all');
              setMarketplaceId('');
              setBrandFilter('');
            }}
          >
            Clear
          </Button>
        )}
        {/* Downloads the whole filtered set, not the page on screen — the reason it exists is
            that the page is 25 rows of several thousand. Same filters, so the file is what is
            being looked at. */}
        <Tooltip content={
          pagination.totalCount === 0
            ? 'Nothing to export with these filters.'
            : `Downloads all ${num(pagination.totalCount)} matching rows as a CSV, not just this page.`
        }>
          <Button
            size="slim"
            icon={ExportIcon}
            loading={exporting}
            disabled={exporting || pagination.totalCount === 0}
            onClick={exportCsv}
          >
            Export CSV
          </Button>
        </Tooltip>
        {/* Scoped to one retailer on purpose — see openBulkPush. Disabled rather than hidden
            when writes are off or no retailer is chosen, so the reason is readable instead of
            the control simply being absent. */}
        <Tooltip content={
          pushDisabledReason
            || (!marketplaceId
              ? 'Choose a single retailer first. Stock goes to a retailer as one import, so a push cannot span several accounts.'
              : 'Sends this retailer every listed SKU whose stock differs from what they hold. Asks first, and tells you how many and how old the Shopify figures are.')
        }>
          <Button
            size="slim"
            variant="primary"
            tone="critical"
            loading={pushBusy && !pushPreview}
            disabled={Boolean(pushDisabledReason) || !marketplaceId || pushBusy}
            onClick={openBulkPush}
          >
            Push stock
          </Button>
        </Tooltip>
      </InlineStack>

      {summary && (
        <BlockStack gap="050">
          <InlineStack gap="200" wrap blockAlign="center">
            {/* The summary deliberately ignores the Status filter — it is computed without it
                so these figures describe the whole set and can act as filter chips. That made
                it read as a contradiction: "40 stock differs" above 10 rows. So the MATCHING
                count is stated first when a filter is on, and the rest is labelled as the
                wider set it is. pagination.totalCount was already fetched and never shown. */}
            {statusFilterActive && (
              <>
                <Text variant="bodySm" fontWeight="semibold">
                  {num(pagination.totalCount)} matching
                </Text>
                <Text variant="bodySm" tone="subdued">&middot;</Text>
                <Text variant="bodySm" tone="subdued">of</Text>
              </>
            )}
            <Text variant="bodySm" tone="subdued">
              {num(summary.total)} SKU{summary.total === 1 ? '' : 's'}
            </Text>
            <Text variant="bodySm" tone="subdued">&middot;</Text>
            <Text variant="bodySm" tone="subdued">{num(summary.listed)} listed</Text>
            <Text variant="bodySm" tone="subdued">&middot;</Text>
            <Text variant="bodySm" tone="subdued">{num(summary.notListed)} not listed</Text>
            <Text variant="bodySm" tone="subdued">&middot;</Text>
            <Text variant="bodySm" tone="subdued">{num(summary.inactive)} inactive</Text>
            <Text variant="bodySm" tone="subdued">&middot;</Text>
            <Text variant="bodySm" tone="subdued">{num(summary.mismatched)} stock differs</Text>
            {summary.mismatchedUnreliable > 0 && (
              <>
                <Text variant="bodySm" tone="subdued">&middot;</Text>
                <Text variant="bodySm" tone="subdued">
                  {num(summary.mismatchedUnreliable)} unreliable
                </Text>
              </>
            )}
            <Text variant="bodySm" tone="subdued">&middot;</Text>
            <Text variant="bodySm" tone="subdued">{num(summary.pending)} push queued</Text>
            {summary.rejected > 0 && (
              <>
                <Text variant="bodySm" tone="subdued">&middot;</Text>
                <Text variant="bodySm" tone="critical" fontWeight="semibold">
                  {num(summary.rejected)} push rejected
                </Text>
              </>
            )}
          </InlineStack>
          {unmatched.map((u) => (
            <Text key={u.marketplaceId} variant="bodySm" tone="caution">
              {u.marketplaceName} lists {num(u.count)} SKU{u.count === 1 ? '' : 's'} that {u.count === 1 ? 'is' : 'are'} not
              in the Shopify catalogue — those offers cannot be fulfilled and are not in the table below.
            </Text>
          ))}
        </BlockStack>
      )}

      {/* The headings stay visible when there are no rows: IndexTable's emptyState replaces the
          whole table at itemCount 0, headings included (IndexTable.js:353), and a table with no
          column names reads as broken rather than empty. So itemCount is forced to 1 and the
          message is one full-width row instead. */}
      <IndexTable
        resourceName={{ singular: 'SKU', plural: 'SKUs' }}
        itemCount={rows.length || 1}
        headings={headings}
        sortable={sortable}
        sortToggleLabels={sortToggleLabels}
        sortColumnIndex={sortColumnIndex === -1 ? undefined : sortColumnIndex}
        sortDirection={sortDirection === 'desc' ? 'descending' : 'ascending'}
        onSort={(index, direction) => {
          const col = columns[index];
          if (!col?.sort) return;
          setSortBy(col.sort);
          setSortDirection(direction === 'descending' ? 'desc' : 'asc');
        }}
        loading={loading}
        selectable={false}
      >
        {rows.length === 0 && (
          <IndexTable.Row id="no-skus" position={0}>
            <IndexTable.Cell colSpan={columns.length}>
              <Box padding="500">
                <Text alignment="center" tone="subdued">
                  {/* An error must not read as "no data". On a failed first load `data` is
                      null, so rows are empty and `filtered` is false — which used to print
                      "No SKUs yet, resync the catalogue" directly beneath a red error
                      banner, telling the admin everything was fine and handing them the
                      wrong remedy. */}
                  {error && !data
                    ? 'Could not load the comparison — see the error above.'
                    : filtered
                      ? 'No SKUs match these filters.'
                      // Says WHICH step is missing. This table only holds brands with an
                      // enabled retailer connection — without one there is no retailer side
                      // to compare against — so "resync the catalogue" was the wrong remedy
                      // for the commonest reason it is empty.
                      : 'No SKUs to compare. Only brands connected to a retailer appear here — '
                        + 'connect one in Brands above. If a brand is already '
                        + 'connected, resync its Shopify catalogue and fetch the retailer\'s SKUs.'}
                </Text>
              </Box>
            </IndexTable.Cell>
          </IndexTable.Row>
        )}
        {rows.map((row, i) => {
          const key = rowKey(row);
          const diff = row.difference;
          /**
           * Red when the two sides disagree — the whole row, so a difference is found by
           * scanning rather than by reading a column.
           *
           * EXACTLY the test behind the "stock differs" count above (see the `mismatched`
           * filter in sku-map-service): a difference that exists, is not zero, and is not
           * built on a Shopify total we know understates. Any other test here would put a
           * number and a colour on the same screen saying different things, which is the
           * thing this table is least allowed to do.
           *
           * A push already queued or in flight does NOT clear the red. The stock still
           * disagrees right now, the count above still includes it, and the Last push column
           * is where "it is being dealt with" is said.
           */
          const differs = diff != null && diff !== 0 && row.differencePartial !== true;

          /**
           * The previous send, shown only where it adds something.
           *
           * Off a clean finished push, whose own timestamp is already on the row — the line
           * would print the same moment twice. Off an attempt in flight, because that attempt
           * IS the last send. What is left is the queued, the rejected and the unconfirmed:
           * the three states that say something is outstanding, and the three where a reader
           * needs to know whether this SKU has ever reached the retailer before.
           */
          const inFlight = row.pendingStatus === 'SUBMITTED';
          const finishedClean = row.pendingStatus === 'RESOLVED' && !row.pendingError;
          const showLastSent = Boolean(row.pendingLastSentAt) && !inFlight && !finishedClean;
          return (
            <IndexTable.Row id={key} key={key} position={i} tone={differs ? 'critical' : undefined}>
              <IndexTable.Cell>
                <BlockStack gap="050">
                  <Text variant="bodyMd" fontWeight="semibold" numeric>{row.sku}</Text>
                  {row.productTitle && (
                    <Text variant="bodySm" tone="subdued">
                      {row.productTitle}
                      {row.variantTitle && row.variantTitle !== 'Default Title' ? ` — ${row.variantTitle}` : ''}
                    </Text>
                  )}
                  {/* Shopify allows the same SKU on several items, and the development store has
                      480 that do. The stock figure is their total, which only means something if
                      you are told that. */}
                  {row.variantCount > 1 && (
                    <Text variant="bodySm" tone="caution">
                      Shared by {row.variantCount} Shopify items
                      {row.productCount > 1 ? ` across ${row.productCount} products` : ''}
                      {' — stock is their total'}
                      {row.productCount > 1 ? ', and the title above is one of them' : ''}
                    </Text>
                  )}
                </BlockStack>
              </IndexTable.Cell>
              {showBrandColumn && (
                <IndexTable.Cell>
                  <Text variant="bodySm">{row.brandName || '—'}</Text>
                </IndexTable.Cell>
              )}
              <IndexTable.Cell>
                {row.marketplaceName
                  ? <Text variant="bodyMd">{row.marketplaceName}</Text>
                  : <Text variant="bodySm" tone="subdued">Not listed</Text>}
              </IndexTable.Cell>
              <IndexTable.Cell>
                {/* A SKU whose stock has never been read is not zero, and printing 0 for it
                    would invite a push of the wrong figure. */}
                {row.shopifyQuantityPartial
                  ? <Tooltip content="At least one Shopify item behind this SKU has no stock figure yet, so this total is incomplete. Re-read the SKU or resync the catalogue."><Text variant="bodyMd" tone="subdued">{num(row.shopifyQuantity)}?</Text></Tooltip>
                  : <Text variant="bodyMd" numeric>{num(row.shopifyQuantity)}</Text>}
              </IndexTable.Cell>
              <IndexTable.Cell>
                {row.miraklQuantity == null
                  ? <Text variant="bodySm" tone="subdued">—</Text>
                  : <Text variant="bodyMd" numeric>{num(row.miraklQuantity)}</Text>}
              </IndexTable.Cell>
              <IndexTable.Cell>
                {diff == null && <Text variant="bodySm" tone="subdued">—</Text>}
                {diff === 0 && <Text variant="bodyMd" tone="subdued" numeric>0</Text>}
                {/* A queued or in-flight push explains the gap, so the number reads
                    quietly. A REJECTED push does not explain it — it is the reason the gap
                    will stay — so that case keeps the caution tone. */}
                {diff != null && diff !== 0 && (
                  row.differencePartial ? (
                    // Built on a Shopify total that understates, because at least one item
                    // behind the SKU has no stock figure yet. Marked, and excluded from the
                    // "stock differs" count — a gap invented by unread stock is not a gap.
                    <Tooltip content="At least one Shopify item behind this SKU has no stock figure yet, so this difference is not reliable. Re-read the SKU, or resync the catalogue.">
                      <Text variant="bodyMd" tone="subdued" numeric>{signed(diff)}?</Text>
                    </Tooltip>
                  ) : (
                    // No tone of its own: the row is red, and a second colour on the number
                    // inside it was one signal too many — it also went quiet on a queued push
                    // while the row stayed red, so the two disagreed about the same SKU.
                    <Text variant="bodyMd" fontWeight="semibold" numeric>
                      {signed(diff)}
                    </Text>
                  )
                )}
              </IndexTable.Cell>
              <IndexTable.Cell>
                <BlockStack gap="050">
                  {row.status === 'listed' && <Badge tone="success">Listed</Badge>}
                  {row.status === 'inactive' && <Badge tone="warning">Inactive</Badge>}
                  {row.status === 'not_listed' && <Badge>Not listed</Badge>}
                  {row.status === 'inactive' && row.inactivityReasons.length > 0 && (
                    <Text variant="bodySm" tone="subdued">
                      {row.inactivityReasons.join(', ')}
                    </Text>
                  )}
                </BlockStack>
              </IndexTable.Cell>
              <IndexTable.Cell>
                <BlockStack gap="050">
                  <Text variant="bodySm" tone="subdued">
                    Shopify {shortWhen(row.shopifySyncedAt) || '—'}
                  </Text>
                  <Text variant="bodySm" tone="subdued">
                    Retailer {shortWhen(row.miraklVerifiedAt) || '—'}
                  </Text>
                </BlockStack>
              </IndexTable.Cell>
              <IndexTable.Cell>
                <BlockStack gap="050">
                {/* A finished, clean push says SO. It used to fall through to the dash
                    below, which read identically to a SKU never pushed at all — so someone
                    who had just pressed Push stock and watched it reach the retailer could
                    only conclude it had not worked. */}
                {row.pendingStatus === 'RESOLVED' && !row.pendingError && (
                  <Tooltip content="This SKU's stock reached the retailer and they applied it. Nothing is outstanding.">
                    <Text variant="bodySm" tone="subdued">
                      {shortWhen(row.pendingSubmittedAt) || 'Applied'}
                    </Text>
                  </Tooltip>
                )}
                {!row.pendingStatus && (
                  <Tooltip content="No stock has been sent to the retailer for this SKU. A push is queued only when the stock actually changes in Shopify, so a dash on a SKU that has never moved is normal.">
                    <Text variant="bodySm" tone="subdued">—</Text>
                  </Tooltip>
                )}
                {/* A refused push. This is the state that used to be invisible: a batch can
                    be accepted as a whole while the retailer rejects individual SKUs inside
                    it, and every row in the batch was then recorded as landed. The reason
                    comes from the retailer's own error report. */}
                {row.pendingStatus === 'FAILED' && (
                  <Tooltip content={row.pendingError || 'The retailer refused this stock figure. No reason was recorded.'}>
                    <Badge tone="critical">Rejected</Badge>
                  </Tooltip>
                )}
                {/* Landed, but its import refused lines it could not name — so we cannot say
                    this SKU was among the ones that worked. */}
                {row.pendingStatus === 'RESOLVED' && row.pendingError && (
                  <Tooltip content={row.pendingError}>
                    <Badge tone="warning">Unconfirmed</Badge>
                  </Tooltip>
                )}
                {row.pendingStatus === 'PENDING' && (
                  <Tooltip content={`Queued ${shortWhen(row.pendingQueuedAt) || ''} and not sent yet. Inventory pushes go out in batches, and only when Push inventory is switched on in Sync Settings.`}>
                    <Badge tone="attention">{`Queued ${num(row.pendingQuantity)}`}</Badge>
                  </Tooltip>
                )}
                {row.pendingStatus === 'SUBMITTED' && (
                  <Tooltip content="Sent to the retailer in a batch and awaiting its import report. Attempted, not confirmed — a batch can be accepted as a whole while individual SKUs in it are rejected.">
                    <Badge tone="info">{`Attempted ${num(row.pendingQuantity)}`}</Badge>
                  </Tooltip>
                )}
                {/* The unique on a queued push is (retailer, SKU) and carries no brand, so a
                    push queued by a DIFFERENT brand for the same SKU is possible. Saying so is
                    better than filtering it out and leaving the difference unexplained. */}
                {/* WHAT CAME BEFORE THE BADGE ABOVE.
                    There is one push row per (retailer, SKU) and a requeue overwrites it, so
                    the moment a SKU was queued again, every trace that it had ever been
                    pushed went with it: "Queued 66" and nothing else, identical in that
                    respect to a SKU never pushed at all — which is the one thing this column
                    is read to rule out. Left off a clean finished push, whose own timestamp
                    is already the answer, and off an attempt in flight, which IS this send. */}
                {showLastSent && (
                  <Tooltip content="When stock for this SKU last went to the retailer. The badge above is what is happening now — this is the send before it.">
                    <Text variant="bodySm" tone="subdued">{shortWhen(row.pendingLastSentAt)}</Text>
                  </Tooltip>
                )}
                {row.pendingStatus && row.pendingBrandId && row.pendingBrandId !== row.brandId && (
                  <Text variant="bodySm" tone="caution">queued by another brand</Text>
                )}
                </BlockStack>
              </IndexTable.Cell>
              <IndexTable.Cell>
                <InlineStack gap="100" wrap={false}>
                <Tooltip content="Reads this SKU again from both sides right now — Shopify, and the retailer's own offer. Reads only; nothing is sent to the retailer.">
                  <Button
                    size="slim"
                    loading={rereading === key}
                    disabled={Boolean(rereading) && rereading !== key}
                    onClick={() => handleReread(row)}
                  >
                    Re-read
                  </Button>
                </Tooltip>
                {/* Only where the retailer actually lists the SKU. A "Not listed" row has no
                    offer to update, so there is nothing to push at — and marketplaceId is
                    null on those rows, so there would be no account to send it to either. */}
                {row.marketplaceId && (
                  <Tooltip content={
                    pushDisabledReason
                      || `Sends this SKU's Shopify stock to ${row.marketplaceName} now, less any units already committed to accepted retailer orders. Takes effect on their live offer.`
                  }>
                    <Button
                      size="slim"
                      tone="critical"
                      loading={pushingRow === key}
                      disabled={Boolean(pushDisabledReason) || (Boolean(pushingRow) && pushingRow !== key)}
                      onClick={() => handleRowPush(row)}
                    >
                      Push stock
                    </Button>
                  </Tooltip>
                )}
                </InlineStack>
              </IndexTable.Cell>
            </IndexTable.Row>
          );
        })}
      </IndexTable>

      {/* Both controls in one row, and only once there are rows to page through — a pager
          under an empty table is three dead words. The pager itself drops out when everything
          fits, but the row count stays: it is how someone GETS a second page, so hiding it at
          size 50 would strand them there. */}
      {rows.length > 0 && (
        <InlineStack align="space-between" blockAlign="center">
          <PageSizeSelect value={pageSize} onChange={setPageSize} />
          {pagination.totalPages > 1 && (
            /* Driven by the LOCAL page, not the last response's. Mixing them meant a failed
               fetch left the two diverging, and one more Next then advanced by two. */
            <Pagination
              hasPrevious={page > 1}
              onPrevious={() => setPage((p) => Math.max(1, p - 1))}
              hasNext={page < pagination.totalPages && pagination.hasNext !== false}
              onNext={() => setPage((p) => p + 1)}
              label={`Page ${page} of ${num(pagination.totalPages)}`}
            />
          )}
        </InlineStack>
      )}

      {/* Confirmation for the bulk push.
      
          Names the retailer, the count and the AGE of the Shopify figures, because the
          quantities come from the catalogue mirror rather than a live read — committing a
          stale number to someone else's live listing should be a visible choice, not a
          side effect of pressing a button. */}
      {pushPreview && (
        <Modal
          open
          onClose={() => setPushPreview(null)}
          title={`Push stock to ${pushPreview.marketplaceName}?`}
          primaryAction={{
            content: pushPreview.count === 0
              ? 'Nothing to push'
              : `Push ${num(pushPreview.count)} SKU${pushPreview.count === 1 ? '' : 's'}`,
            destructive: true,
            disabled: pushPreview.count === 0 || pushBusy,
            loading: pushBusy,
            onAction: confirmBulkPush,
          }}
          secondaryActions={[{ content: 'Cancel', onAction: () => setPushPreview(null) }]}
        >
          <Modal.Section>
            <BlockStack gap="300">
              {pushPreview.count === 0 ? (
                <Text>
                  Every listed SKU on this retailer already matches the stock we hold, so there
                  is nothing to send.
                </Text>
              ) : (
                <>
                  <Text>
                    {`This updates ${num(pushPreview.count)} live offer${pushPreview.count === 1 ? '' : 's'} at ${pushPreview.marketplaceName}, in one import. There is no undo — only another push.`}
                  </Text>
                  <Text variant="bodySm" tone="subdued">
                    {`Quantities are Shopify stock less any units already committed to accepted retailer orders. Shopify figures were last read ${shortWhen(pushPreview.shopifyLastRead) || 'never'} — run Fetch inventories first if that is too old to commit.`}
                  </Text>
                  {pushPreview.wouldZero > 0 && (
                    <Banner tone="warning">
                      {`${num(pushPreview.wouldZero)} of these would be sent as zero, which delists them at the retailer until stock returns.`}
                    </Banner>
                  )}
                  {pushPreview.sample.length > 0 && (
                    <BlockStack gap="100">
                      <Text variant="bodySm" tone="subdued">First few, so the numbers can be checked:</Text>
                      {pushPreview.sample.map((r) => (
                        <Text key={r.shopSku} variant="bodySm">
                          {`${r.shopSku} — retailer holds ${r.retailerQuantity ?? '—'}, will be set to ${r.quantity}`}
                        </Text>
                      ))}
                    </BlockStack>
                  )}
                  {/* What the count LEAVES OUT. The service reports each exclusion under its
                      own name precisely so none is mislabelled, and the dialog showed none of
                      them — so a push capped at the ceiling looked like the whole catalogue,
                      and an operator confirming 5,000 had no way to know more were waiting. */}
                  {pushPreview.capped?.count > 0 && (
                    <Banner tone="warning">
                      {`${num(pushPreview.capped.count)} more SKU${pushPreview.capped.count === 1 ? '' : 's'} `
                        + 'are beyond the 5,000-SKU ceiling for one import and are NOT included. '
                        + 'Push again after this import to send the rest.'}
                    </Banner>
                  )}
                  {(pushPreview.unlistedRequested?.length > 0
                    || pushPreview.unownedRequested?.length > 0
                    || pushPreview.noMirrorRequested?.length > 0) && (
                    <Text variant="bodySm" tone="subdued">
                      {'Not included: '
                        + [
                          pushPreview.unlistedRequested?.length
                            && `${num(pushPreview.unlistedRequested.length)} not listed at this retailer`,
                          pushPreview.unownedRequested?.length
                            && `${num(pushPreview.unownedRequested.length)} listed but belonging to another brand`,
                          pushPreview.noMirrorRequested?.length
                            && `${num(pushPreview.noMirrorRequested.length)} with no Shopify stock figure yet`,
                        ].filter(Boolean).join(', ')
                        + '.'}
                    </Text>
                  )}
                  {pushPreview.syncInventory === false && (
                    <Banner tone="critical">
                      Inventory sync is switched off for this retailer, so the import will be
                      refused. Turn it on with Edit on the retailer row first.
                    </Banner>
                  )}
                </>
              )}
            </BlockStack>
          </Modal.Section>
        </Modal>
      )}
    </BlockStack>
  );
}
